import React, { useRef } from 'react';
import './SafePulse.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple, faWindows } from '@fortawesome/free-brands-svg-icons';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { 
  ShieldAlert, 
  Wallet, 
  FileCheck, 
  Percent, 
  Ghost, 
  HardDrive, 
  Zap, 
  Activity, 
  Lock, 
  Clock,
  CheckCircle,
  Cpu 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import SafePulseImage from './safepulse-image.png';
import SafePulseImage2 from './safepulse-image2.jpg';
import SafePulseInteractive from './SafePulseInteractive';


const SafePulse = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  
  const scrollToSection = () => {
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToContact = () => {
    navigate('/contact');
  };

  return (
    <div className="sp-page">
      <Helmet>
        <title>SafePulse - Erasys</title>
        <meta
          name="description"
          content="SafePulse: Continuous authentication via typing, mouse, and behavioral biometrics. Non-invasive, locally stored encrypted data. Your digital DNA that cannot be stolen or spoofed."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="sp-hero-section tech-bg">
        <div className="sp-hero-content">
          <h1 className="sp-hero-title">SafePulse</h1>
          <p className="sp-hero-tagline">Your Digital DNA. Impossible to Steal.</p>

          {/* Secondary Links */}
          <div className="sp-links-wrapper">
            <a href="/cybersecurity-risk-assessment" className="sp-secondary-link">
              Take the Cybersecurity Risk Assessment
              <span className="sp-link-arrow">→</span>
            </a>
            <a 
              href="https://www.intel.com/content/www/us/en/content-details/871288/erasys-safepulse-partner-brief.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="sp-secondary-link sp-intel-highlight"
            >
              <span className="sp-intel-badge">Intel® Partner</span>
              Learn About Our Intel Collaboration
              <span className="sp-link-arrow">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* Hero Image Showcase */}
      <section className="sp-hero-showcase">
        <div className="sp-showcase-container">
          <div className="sp-showcase-content">
            <div className="sp-showcase-text">
              <div className="sp-section-label">How It Works</div>
              <h2 className="sp-showcase-title">Authentication That Knows It's You</h2>
              <p className="sp-showcase-description">
                SafePulse creates a unique behavioral profile by analyzing how you naturally interact with your device. This digital fingerprint is impossible to replicate—providing continuous, invisible authentication that works seamlessly in the background.
              </p>
            </div>
            <div className="sp-showcase-image">
              <img src={SafePulseImage} alt="SafePulse Continuous Authentication" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Feature Section */}
      <section className="sp-feature-section">
        <div className="sp-feature-header">
          <h2 className="sp-feature-section-title">Why SafePulse</h2>
          <p className="sp-feature-section-subtitle">Invisible security that adapts to you</p>
        </div>
        <div className="sp-feature-grid">
          <div className="sp-feature-item sp-feature-intel">
            <div className="sp-feature-icon-top">
               <Cpu size={48} color="#ffffff" />
            </div>
            <h1>Intel®</h1>
            <h3>Optimized AI</h3>
            <p>Accelerated by Intel® Core™ Ultra processors for lightning-fast, secure on-device neural processing.</p>
          </div>
          <div className="sp-feature-item">
            <h1>Zero</h1>
            <h3>User Friction</h3>
            <p>No passwords to remember, no tokens to carry. Authentication happens naturally as you work.</p>
          </div>
          <div className="sp-feature-item">
            <h1>Real-Time</h1>
            <h3>Threat Detection</h3>
            <p>Instantly detects unauthorized access attempts and locks out intruders automatically.</p>
          </div>
          <div className="sp-feature-item">
            <h1>Local</h1>
            <h3>Data Privacy</h3>
            <p>All biometric data encrypted and stored locally. Your digital DNA never leaves your device.</p>
          </div>
          <div className="sp-feature-item">
            <h1>24/7</h1>
            <h3>Protection</h3>
            <p>Continuous monitoring provides round-the-clock security without interrupting your workflow.</p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="sp-how-it-works" ref={sectionRef}>
        <div className="sp-how-container">
          <div className="sp-section-label-center">Technology</div>
          <h2 className="sp-how-title">Your Digital DNA</h2>
          <p className="sp-how-subtitle">Three layers of behavioral biometric authentication working together</p>
          <div className="sp-steps-grid">
            <div className="sp-step-card">
              <div className="sp-step-number">1</div>
              <h3 className="sp-step-title">Typing Patterns</h3>
              
            </div>
            <div className="sp-step-card">
              <div className="sp-step-number">2</div>
              <h3 className="sp-step-title">Mouse Patterns</h3>
             
            </div>
            <div className="sp-step-card">
              <div className="sp-step-number">3</div>
              <h3 className="sp-step-title">Behavioral Patterns</h3>
              
            </div>
           
          </div>
        </div>
      </section>

      {/* Seamless Integration Section */}
      <section className="sp-seamless-section">
        <div className="sp-seamless-container">
          <div className="sp-seamless-content">
            <div className="sp-seamless-image-wrapper">
              <img src={SafePulseImage2} alt="Seamless Integration" className="sp-seamless-image" />
            </div>
            <div className="sp-seamless-text-content">
              <div className="sp-section-label">Integration</div>
              <h2 className="sp-seamless-title">Integrates With Your Security Stack</h2>
              <p className="sp-seamless-description">
                SafePulse seamlessly connects with your existing security infrastructure, providing behavioral insights directly to your SOC, SIEM, and security tools. No complex setup, no user training required.
              </p>
              <ul className="sp-seamless-list">
                <li className="sp-seamless-item">
                  <CheckCircle size={24} color="#0071E3" className="sp-check-icon" />
                  <span>SOC & SIEM integration (Splunk, QRadar, ArcSight)</span>
                </li>
                <li className="sp-seamless-item">
                  <CheckCircle size={24} color="#0071E3" className="sp-check-icon" />
                  <span>Microsoft Sentinel native connector</span>
                </li>
                <li className="sp-seamless-item">
                  <CheckCircle size={24} color="#0071E3" className="sp-check-icon" />
                  <span>Antivirus & EDR compatibility (CrowdStrike, SentinelOne)</span>
                </li>
                <li className="sp-seamless-item">
                  <CheckCircle size={24} color="#0071E3" className="sp-check-icon" />
                  <span>Local dashboard & reporting APIs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Security Benefits Section */}
      <section className="sp-benefits-section">
        <div className="sp-benefits-container">
          <div className="sp-section-label-center">ROI & Benefits</div>
          <h2 className="sp-benefits-title">Proven Cost Savings</h2>
          <p className="sp-benefits-subtitle">
            Measurable return on investment across fraud prevention, operational efficiency, regulatory compliance, and insurance premiums.
          </p>
          <div className="sp-benefits-grid">
            <div className="sp-benefit-card">
              <div className="sp-benefit-icon-wrapper">
                <div className="sp-benefit-icon">
                  <ShieldAlert size={36} color="white" strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="sp-benefit-title">85-92% Fraud Reduction</h3>
              <p className="sp-benefit-description">
                Dramatic decrease in credential-based fraud and account takeovers through continuous behavioral verification that cannot be bypassed.
              </p>
            </div>
            <div className="sp-benefit-card">
              <div className="sp-benefit-icon-wrapper">
                <div className="sp-benefit-icon">
                  <Wallet size={36} color="white" strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="sp-benefit-title">60% Lower IT Costs</h3>
              <p className="sp-benefit-description">
                Reduction in password reset costs and IT support overhead. Seamless authentication eliminates helpdesk tickets and user friction.
              </p>
            </div>
            <div className="sp-benefit-card">
              <div className="sp-benefit-icon-wrapper">
                <div className="sp-benefit-icon">
                  <FileCheck size={36} color="white" strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="sp-benefit-title">45% Fewer Audit Findings</h3>
              <p className="sp-benefit-description">
                Decrease in compliance audit findings related to access control. Meet NIST SP 800-63B requirements with hardware-rooted security.
              </p>
            </div>
            <div className="sp-benefit-card">
              <div className="sp-benefit-icon-wrapper">
                <div className="sp-benefit-icon">
                  <Percent size={36} color="white" strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="sp-benefit-title">25-40% Insurance Savings</h3>
              <p className="sp-benefit-description">
                Reduction in cybersecurity insurance premiums. Insurers recognize continuous authentication as a premium risk mitigation control.
              </p>
            </div>
            <div className="sp-benefit-card">
              <div className="sp-benefit-icon-wrapper">
                <div className="sp-benefit-icon">
                  <Ghost size={36} color="white" strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="sp-benefit-title">Non-Invasive Security</h3>
              <p className="sp-benefit-description">
                Zero user friction. Authentication happens invisibly as users work naturally—no tokens, no interruptions, no password fatigue.
              </p>
            </div>
            <div className="sp-benefit-card">
              <div className="sp-benefit-icon-wrapper">
                <div className="sp-benefit-icon">
                  <HardDrive size={36} color="white" strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="sp-benefit-title">Local Data Storage</h3>
              <p className="sp-benefit-description">
                All biometric data encrypted and stored locally on device. Your digital DNA never leaves your hardware—complete privacy guaranteed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="sp-use-cases">
        <div className="sp-cases-container">
          <div className="sp-section-label-center-light">Use Cases</div>
          <h2 className="sp-cases-title">Industry Solutions</h2>
          <p className="sp-cases-subtitle">Tailored security for every sector</p>
          <div className="sp-cases-grid">
            <div className="sp-case-card">
              <div className="sp-case-number">1</div>
              <h3 className="sp-case-title">Financial Services & Banking</h3>
              <p className="sp-case-description">
                Stop account takeovers and transaction fraud with continuous behavioral verification. Protect customer accounts and prevent unauthorized transfers.
              </p>
              <div className="sp-case-benefit">Eliminates credential-based fraud</div>
              <div className="sp-case-benefit">Real-time transaction verification</div>
              <div className="sp-case-benefit">Regulatory compliance built-in</div>
            </div>
            <div className="sp-case-card">
              <div className="sp-case-number">2</div>
              <h3 className="sp-case-title">Enterprise & Remote Work</h3>
              <p className="sp-case-description">
                Protect corporate data on every endpoint, seamlessly. Secure distributed workforces with continuous authentication that adapts to each user.
              </p>
              <div className="sp-case-benefit">Zero-trust endpoint security</div>
              <div className="sp-case-benefit">No user friction or training</div>
              <div className="sp-case-benefit">Hardware-rooted protection</div>
            </div>
            <div className="sp-case-card">
              <div className="sp-case-number">3</div>
              <h3 className="sp-case-title">Government & Defence</h3>
              <p className="sp-case-description">
                Meet NIST SP 800-63B requirements with a hardware-rooted solution. Continuous authentication for classified systems and sensitive operations.
              </p>
              <div className="sp-case-benefit">NIST SP 800-63B compliant</div>
              <div className="sp-case-benefit">Hardware-based security</div>
              <div className="sp-case-benefit">Audit-ready logging</div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <SafePulseInteractive />

      {/* Platform Section */}
      <section className="sp-platform-section">
        <div className="sp-platform-content">
          <h2>Ready to Experience Invisible Security?</h2>
          <p>Download SafePulse and protect your digital identity with behavioral biometric authentication.</p>
          <div className="sp-platform-buttons">
            <div className="sp-platform-btn-container">
              <button className="sp-platform-btn sp-trial-btn" onClick={goToContact}>
                <FontAwesomeIcon icon={faKey} size="2x" />
              </button>
              <p className="sp-platform-text">Free Trial</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SafePulse;
